import { XamanAuth } from './xaman-auth';

interface NFTMetadata {
    name?: string;
    description?: string;
    image?: string;
    attributes?: Array<{
        trait_type: string;
        value: string;
    }>;
    properties?: {
        rarity?: string;
        edition?: number;
        totalSupply?: number;
    };
}

export class NFTViewer {
    private auth: XamanAuth;
    private container: HTMLElement | null = null;

    constructor(auth: XamanAuth) {
        this.auth = auth;
    }

    /**
     * Initialize the NFT viewer in a container
     */
    init(containerId: string): void {
        this.container = document.getElementById(containerId);
        if (!this.container) {
            console.error(`Container ${containerId} not found`);
            return;
        }
    }

    /**
     * Load and display user's NFTs
     */
    async loadUserNFTs(): Promise<void> {
        if (!this.container) {
            console.error('Container not initialized');
            return;
        }

        if (!this.auth.isLoggedIn()) {
            this.showMessage('Please login with Xaman wallet first');
            return;
        }

        try {
            this.showLoading();
            const nfts = await this.auth.getUserNFTs();

            if (nfts.length === 0) {
                this.showMessage('No Aliens On XRPL NFTs found in your wallet');
                return;
            }

            this.displayNFTs(nfts);
        } catch (error) {
            console.error('Error loading NFTs:', error);
            this.showMessage('Error loading NFTs. Please try again.');
        }
    }

    /**
     * Display NFTs in grid
     */
    private displayNFTs(nfts: any[]): void {
        if (!this.container) return;

        const grid = document.createElement('div');
        grid.className = 'nft-grid';
        grid.style.cssText = `
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 30px;
            margin-top: 30px;
        `;

        nfts.forEach(nft => {
            const card = this.createNFTCard(nft);
            grid.appendChild(card);
        });

        this.container.innerHTML = '';
        this.container.appendChild(grid);
    }

    /**
     * Create NFT card element
     */
    private createNFTCard(nft: any): HTMLElement {
        const metadata: NFTMetadata = nft.metadata || {};

        // Use image_url if available, otherwise fall back to image
        const imageUrl = nft.imageUrl || nft.metadata?.image_url || '/img/logo.png';

        const card = document.createElement('div');
        card.className = 'nft-card';
        card.style.cssText = `
            background: rgba(255, 255, 255, 0.05);
            border: 2px solid var(--primary);
            border-radius: 15px;
            overflow: hidden;
            transition: all 0.3s ease;
            cursor: pointer;
        `;

        card.innerHTML = `
            <div class="nft-image-container" style="position: relative; overflow: hidden;">
                <img src="${imageUrl}"
                     alt="${metadata.name || 'Alien NFT'}"
                     style="width: 100%; height: 280px; object-fit: cover;"
                     onerror="this.src='/img/logo.png'">
                ${metadata.properties?.rarity ? `
                    <div class="nft-rarity" style="
                        position: absolute;
                        top: 10px;
                        right: 10px;
                        background: linear-gradient(135deg, var(--primary), var(--secondary));
                        color: var(--dark);
                        padding: 5px 15px;
                        border-radius: 20px;
                        font-size: 12px;
                        font-weight: bold;
                        font-family: 'Orbitron', sans-serif;
                    ">
                        ${metadata.properties.rarity}
                    </div>
                ` : ''}
            </div>
            <div class="nft-info" style="padding: 20px;">
                <h3 style="
                    color: var(--primary);
                    font-family: 'Orbitron', sans-serif;
                    font-size: 18px;
                    margin-bottom: 10px;
                ">
                    ${metadata.name || 'Unknown Alien'}
                </h3>
                ${metadata.properties?.edition ? `
                    <p style="
                        color: var(--text-gray);
                        font-family: 'Orbitron', sans-serif;
                        font-size: 14px;
                        margin-bottom: 15px;
                    ">
                        Edition ${metadata.properties.edition} / ${metadata.properties.totalSupply || '1000'}
                    </p>
                ` : ''}
                ${metadata.attributes ? `
                    <div class="nft-attributes" style="
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 10px;
                        margin-top: 15px;
                    ">
                        ${metadata.attributes.slice(0, 4).map(attr => `
                            <div style="
                                background: rgba(255, 255, 255, 0.03);
                                padding: 8px;
                                border-radius: 8px;
                                border: 1px solid rgba(255, 255, 255, 0.1);
                            ">
                                <div style="
                                    font-size: 11px;
                                    color: var(--text-gray);
                                    font-family: 'Orbitron', sans-serif;
                                    margin-bottom: 3px;
                                ">
                                    ${attr.trait_type}
                                </div>
                                <div style="
                                    font-size: 13px;
                                    color: var(--light);
                                    font-family: 'Orbitron', sans-serif;
                                    font-weight: 500;
                                ">
                                    ${attr.value}
                                </div>
                            </div>
                        `).join('')}
                    </div>
                ` : ''}
            </div>
        `;

        // Add hover effect
        card.addEventListener('mouseenter', () => {
            card.style.transform = 'translateY(-10px)';
            card.style.boxShadow = '0 20px 40px rgba(0, 255, 136, 0.3)';
        });

        card.addEventListener('mouseleave', () => {
            card.style.transform = 'translateY(0)';
            card.style.boxShadow = 'none';
        });

        // Add click to expand
        card.addEventListener('click', () => {
            this.showNFTDetails(nft);
        });

        return card;
    }

    /**
     * Show NFT details in modal
     */
    private showNFTDetails(nft: any): void {
        const metadata: NFTMetadata = nft.metadata || {};

        const modal = document.createElement('div');
        modal.className = 'nft-modal';
        modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.9);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10000;
            padding: 20px;
        `;

        modal.innerHTML = `
            <div class="modal-content" style="
                background: rgba(10, 14, 39, 0.98);
                border: 2px solid var(--primary);
                border-radius: 20px;
                max-width: 800px;
                width: 100%;
                max-height: 90vh;
                overflow-y: auto;
                padding: 30px;
                position: relative;
            ">
                <button class="modal-close" style="
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    background: transparent;
                    border: none;
                    color: var(--light);
                    font-size: 30px;
                    cursor: pointer;
                    transition: transform 0.3s ease;
                " onmouseover="this.style.transform='rotate(90deg)'" onmouseout="this.style.transform='rotate(0deg)'">×</button>

                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px;">
                    <div>
                        <img src="${nft.imageUrl || '/img/logo.png'}"
                             alt="${metadata.name || 'Alien NFT'}"
                             style="width: 100%; border-radius: 15px; border: 2px solid var(--primary);">
                    </div>
                    <div>
                        <h2 style="
                            color: var(--primary);
                            font-family: 'Orbitron', sans-serif;
                            font-size: 28px;
                            margin-bottom: 15px;
                        ">${metadata.name || 'Unknown Alien'}</h2>

                        ${metadata.description ? `
                            <p style="
                                color: var(--text-gray);
                                font-family: 'Orbitron', sans-serif;
                                font-size: 14px;
                                margin-bottom: 20px;
                                line-height: 1.6;
                            ">${metadata.description}</p>
                        ` : ''}

                        ${metadata.properties?.edition ? `
                            <div style="
                                background: rgba(255, 255, 255, 0.05);
                                padding: 15px;
                                border-radius: 10px;
                                margin-bottom: 20px;
                                border: 1px solid rgba(255, 255, 255, 0.1);
                            ">
                                <div style="color: var(--text-gray); font-size: 12px; margin-bottom: 5px;">Edition</div>
                                <div style="color: var(--primary); font-size: 20px; font-weight: bold;">
                                    ${metadata.properties.edition} / ${metadata.properties.totalSupply || '1000'}
                                </div>
                            </div>
                        ` : ''}

                        ${metadata.attributes && metadata.attributes.length > 0 ? `
                            <h3 style="
                                color: var(--light);
                                font-family: 'Orbitron', sans-serif;
                                font-size: 18px;
                                margin-bottom: 15px;
                            ">Attributes</h3>
                            <div style="display: grid; grid-template-columns: 1fr; gap: 10px;">
                                ${metadata.attributes.map(attr => `
                                    <div style="
                                        background: rgba(255, 255, 255, 0.03);
                                        padding: 12px;
                                        border-radius: 8px;
                                        border: 1px solid rgba(255, 255, 255, 0.1);
                                        display: flex;
                                        justify-content: space-between;
                                    ">
                                        <span style="color: var(--text-gray); font-size: 13px;">${attr.trait_type}</span>
                                        <span style="color: var(--primary); font-size: 13px; font-weight: 500;">${attr.value}</span>
                                    </div>
                                `).join('')}
                            </div>
                        ` : ''}
                    </div>
                </div>
            </div>
        `;

        // Close modal on click outside or close button
        modal.addEventListener('click', (e) => {
            if (e.target === modal || (e.target as HTMLElement).classList.contains('modal-close')) {
                document.body.removeChild(modal);
            }
        });

        document.body.appendChild(modal);
    }

    /**
     * Show loading state
     */
    private showLoading(): void {
        if (!this.container) return;

        this.container.innerHTML = `
            <div style="
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 60px;
            ">
                <div class="alien-loader" style="
                    width: 60px;
                    height: 60px;
                    border: 5px solid rgba(0, 255, 136, 0.2);
                    border-top-color: var(--primary);
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                "></div>
                <p style="
                    color: var(--text-gray);
                    font-family: 'Orbitron', sans-serif;
                    margin-top: 20px;
                    font-size: 16px;
                ">Loading your aliens...</p>
            </div>
        `;

        // Add spin animation
        const style = document.createElement('style');
        style.textContent = `
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Show message
     */
    private showMessage(message: string): void {
        if (!this.container) return;

        this.container.innerHTML = `
            <div style="
                text-align: center;
                padding: 60px 20px;
            ">
                <img src="/img/logo.png" alt="AOX" style="width: 80px; height: 80px; margin-bottom: 20px; opacity: 0.5;">
                <p style="
                    color: var(--text-gray);
                    font-family: 'Orbitron', sans-serif;
                    font-size: 16px;
                ">${message}</p>
            </div>
        `;
    }

    /**
     * Clear the viewer
     */
    clear(): void {
        if (this.container) {
            this.container.innerHTML = '';
        }
    }
}
